import { useCallback, useEffect, useRef, useState } from "react";

import {
  ArrowRightIcon,
  CheckIcon,
  MinusIcon,
  PlusIcon,
  V2Button,
  V2Heading,
  V2Tooltip,
  XIcon,
} from "@stacklet/ui";
import { graphql, useLazyLoadQuery, useRefetchableFragment } from "react-relay";

import { Avatar } from "app/components/avatar/Avatar";
import { ExportCsv } from "app/components/csv-export/ExportCsv";
import { HumanTimestamp } from "app/components/HumanTimestamp";
import { Link } from "app/components/links";
import { StackletGrid } from "app/components/table/StackletGrid";
import { SEARCH_DEBOUNCE_MS } from "app/constants";
import { useCursorPagination } from "app/hooks/grid";
import { createMultipleFilter, createSingleFilter } from "app/utils/filters";

import type { GroupUserBrowser_users$key } from "./__generated__/GroupUserBrowser_users.graphql";
import type { GroupUserBrowserQuery } from "./__generated__/GroupUserBrowserQuery.graphql";
import type { GroupUserBrowserRefetchQuery } from "./__generated__/GroupUserBrowserRefetchQuery.graphql";
import type { GridColDef } from "@mui/x-data-grid-pro";
import type { CursorVariables } from "app/hooks/grid";
import type { ExportConnectionInput } from "app/mutations/__generated__/ExportConnectionMutation.graphql";
import type { FilterElementInput } from "app/utils/filters";

/** Selectable page sizes for the user browser */
const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100] as const;
/** Default number of users per page */
const DEFAULT_PAGE_SIZE = 5;
/** Width of the add/remove column, sized to the icon button it holds */
const TRANSFER_COLUMN_WIDTH = 44;
/** Fields the grid must leave at their declared width. Stable identity. */
const FIXED_WIDTH_FIELDS = ["transfer"];

/** Columns included in the CSV export of the group's users */
const CSV_COLUMNS = [
  { name: "Name", path: "name" },
  { name: "Email", path: "email" },
  { name: "Last Login", path: "lastLogin" },
] as const;

// Match the design's grid chrome: space-xs cell padding, a short header row with
// small secondary-coloured labels, and no vertical padding fighting the fixed
// row height.
const GRID_CLASS_NAMES = [
  "[&_.MuiDataGrid-cell]:!px-xs",
  "[&_.MuiDataGrid-columnHeader]:!px-xs",
  "[&_.MuiDataGrid-columnHeader]:!text-text-secondary",
  "[&_.MuiDataGrid-columnHeaderTitle]:!text-label-small",
].join(" ");

/**
 * Which slice of the user directory a browser lists, relative to one group:
 * its direct members, or everyone who isn't one.
 */
export type GroupUserScope = "members" | "non-members";

/** A user as handed to {@link Props.onTransfer} */
export interface GroupUser {
  /** Relay node id, for invalidating the user's cached record */
  id: string;
  /** Stable identifier, used to add/remove the user as a group member */
  uuid: string;
  /** Human label for alerts and accessible names */
  label: string;
}

/**
 * Negates a filter. `createMultipleFilter` collapses a single-operand group to
 * that operand, which would silently drop the negation, so build it directly.
 */
function negate(
  filter: FilterElementInput | undefined,
): FilterElementInput | undefined {
  return filter
    ? { multiple: { operator: "NOT", operands: [filter] } }
    : filter;
}

/**
 * Builds the filter for one side of a group's membership, optionally narrowed
 * by a search term. Membership means direct membership of the named group — the
 * `sso-group` filter resolves through the group-member table, so it covers
 * locally-managed groups too — and search matches against the username.
 */
export function buildFilter(
  groupName: string,
  search: string,
  scope: GroupUserScope = "members",
): FilterElementInput | undefined {
  const term = search.trim();
  const isMember = createSingleFilter("sso-group", groupName, "array_contains");
  return createMultipleFilter([
    scope === "non-members" ? negate(isMember) : isMember,
    term ? createSingleFilter("username", term, "contains") : undefined,
  ]);
}

/** A single row in the user grid */
interface UserRow {
  id: string;
  uuid: string;
  key: number;
  name?: string | null;
  email?: string | null;
  lastLogin?: string | null;
  isSsoUser: boolean;
  hasApiKeys: boolean;
}

/** Props for the GroupUserBrowser component */
interface Props {
  /** Name of the group whose membership defines the list */
  groupName: string;
  /** Which users to list; defaults to the group's direct members */
  scope?: GroupUserScope;
  /** Heading above the grid; pass null to render none */
  title?: string | null;
  /**
   * Explanatory line under the heading. Defaults to none: what the list means
   * depends on how the group is managed, which only the caller knows.
   */
  description?: string | null;
  /** Whether to offer a CSV export of the (filtered) list */
  enableExport?: boolean;
  /**
   * Search term supplied by the parent. Providing it hands search over to the
   * caller: the grid's own toolbar (and its search field) is hidden and the
   * term is applied as given, so the caller owns any debouncing.
   */
  searchTerm?: string;
  /** Storage key for the grid's sticky column state; unique per rendered grid */
  tableId?: string;
  /**
   * Row action that moves the user in or out of the group. Supplying it adds a
   * leading column of `+` (for a non-member) or `−` (for a member) buttons.
   */
  onTransfer?: (user: GroupUser) => void;
  /** Disables the transfer buttons, e.g. while a membership change is in flight */
  isTransferDisabled?: boolean;
  /** Tooltip explaining why the transfer buttons are disabled */
  transferDisabledReason?: string;
  /**
   * Changing this value refetches the first page. Used to resync the list when
   * something outside it — a membership change made on the other list —
   * invalidates what it is showing.
   */
  refreshToken?: number;
}

/**
 * Browses users on one side of a group's membership: a searchable, sortable,
 * cursor-paginated grid showing an avatar, name and email, whether they hold
 * API keys and their last login, with a link through to each user's details
 * page and CSV export of the (filtered) list.
 *
 * By default it lists the group's members with its own search field, heading
 * and export — the read-only view on the group details page. The optional props
 * let it double as one half of a transfer list: `scope` picks members or
 * non-members, `searchTerm` hands search to a parent driving both halves from
 * one field, and `onTransfer` adds the add/remove column. See
 * {@link GroupMembershipTransferList}.
 *
 * Sorting, column controls and the toolbar come from the shared XGridTable
 * (MUI DataGrid). Pagination replaces the page on each navigation (rather than
 * appending), so it uses `useRefetchableFragment` with explicit forward
 * (`first`/`after`) and backward (`last`/`before`) cursors instead of a
 * `@connection`-backed pagination fragment.
 */
export function GroupUserBrowser({
  groupName,
  scope = "members",
  title = "Users",
  description = null,
  enableExport = true,
  searchTerm,
  tableId = "group-user-browser",
  onTransfer,
  isTransferDisabled = false,
  transferDisabledReason,
  refreshToken,
}: Props) {
  // With a `searchTerm` the parent owns the term (and its debounce); otherwise
  // the browser renders its own field and debounces internally.
  const isSearchControlled = searchTerm !== undefined;

  // `search` is the live input value; `appliedSearchRef` holds the term the
  // currently loaded page (and its cursors) was fetched with. Pagination builds
  // its filter from the ref so a not-yet-debounced keystroke can't pair a new
  // filter with a cursor from the old result set — and reading a ref (rather
  // than state) keeps the navigation callbacks stable.
  const [search, setSearch] = useState(searchTerm ?? "");
  const appliedSearchRef = useRef(searchTerm ?? "");
  const [exportInProgress, setExportInProgress] = useState(false);
  const [exportParams, setExportParams] =
    useState<ExportConnectionInput["params"]>(undefined);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Clear any pending search debounce on unmount so a timer scheduled just
  // before navigating away can't fire a refetch (and state update) on the
  // unmounted component or retain its closure until the timer elapses.
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // Read the query variables once: every later change (search, refresh, paging)
  // goes through `refetch`, which keeps the loaded page mounted rather than
  // re-suspending the whole browser.
  const initialVariablesRef = useRef({
    filterElement: buildFilter(groupName, searchTerm ?? "", scope),
    first: DEFAULT_PAGE_SIZE,
  });

  const queryData = useLazyLoadQuery<GroupUserBrowserQuery>(
    graphql`
      query GroupUserBrowserQuery(
        $filterElement: FilterElementInput
        $first: Int
      ) {
        ...GroupUserBrowser_users
          @arguments(filterElement: $filterElement, first: $first)
      }
    `,
    initialVariablesRef.current,
  );

  const [data, refetch] = useRefetchableFragment<
    GroupUserBrowserRefetchQuery,
    GroupUserBrowser_users$key
  >(
    graphql`
      fragment GroupUserBrowser_users on Query
      @argumentDefinitions(
        first: { type: "Int" }
        last: { type: "Int" }
        after: { type: "String" }
        before: { type: "String" }
        filterElement: { type: "FilterElementInput" }
      )
      @refetchable(queryName: "GroupUserBrowserRefetchQuery") {
        users(
          first: $first
          last: $last
          after: $after
          before: $before
          filterElement: $filterElement
        ) {
          edges {
            node {
              id
              uuid
              key
              name
              email
              lastLogin
              ssoUser
              apiKeys {
                pageInfo {
                  total
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
            total
          }
        }
      }
    `,
    queryData,
  );

  const rows: UserRow[] = (data.users.edges ?? [])
    .map((edge) => edge.node)
    .filter((node): node is NonNullable<typeof node> => Boolean(node))
    .map((node) => ({
      id: node.id,
      uuid: node.uuid,
      key: node.key,
      name: node.name,
      email: node.email,
      lastLogin: node.lastLogin,
      isSsoUser: node.ssoUser,
      hasApiKeys: Boolean(node.apiKeys.pageInfo.total),
    }));

  const { hasNextPage, hasPreviousPage, total } = data.users.pageInfo;

  // Every refetch carries the active filter (built from the applied search) so
  // paging never drops the current search; reading the ref keeps this stable.
  const onNavigate = useCallback(
    (variables: CursorVariables) =>
      refetch(
        {
          ...variables,
          filterElement: buildFilter(
            groupName,
            appliedSearchRef.current,
            scope,
          ),
        },
        { fetchPolicy: "network-only" },
      ),
    [refetch, groupName, scope],
  );

  // Any explicit navigation supersedes a pending debounced search, so cancel it
  // before paging — otherwise its stale callback fires later and resets the
  // page/filter, replacing the navigation just made.
  const onBeforeNavigate = useCallback(
    () => clearTimeout(debounceRef.current),
    [],
  );

  const {
    pageSize,
    isPending,
    rangeStart,
    rangeEnd,
    handleNext,
    handlePrevious,
    handlePageSizeChange,
    goToFirstPage,
  } = useCursorPagination({
    pageInfo: data.users.pageInfo,
    rowCount: rows.length,
    defaultPageSize: DEFAULT_PAGE_SIZE,
    onNavigate,
    onBeforeNavigate,
  });

  // A parent-driven search term or refresh token invalidates the loaded page,
  // so refetch it from the start. The ref (rather than the dependency list
  // alone) decides whether anything actually changed, so a fresh
  // `goToFirstPage` identity after a fetch can't retrigger the refetch that
  // fetch just satisfied.
  const lastRefetchedRef = useRef({ searchTerm, refreshToken });
  useEffect(() => {
    const last = lastRefetchedRef.current;
    if (last.searchTerm === searchTerm && last.refreshToken === refreshToken) {
      return;
    }
    lastRefetchedRef.current = { searchTerm, refreshToken };
    if (searchTerm !== undefined) {
      appliedSearchRef.current = searchTerm;
    }
    goToFirstPage();
  }, [searchTerm, refreshToken, goToFirstPage]);

  const isMemberList = scope === "members";

  const columns: GridColDef<UserRow>[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 240,
      renderCell: ({ row }) => (
        <div className="flex h-full items-center gap-sm">
          <Avatar
            email={row.email ?? ""}
            name={row.name ?? row.email ?? ""}
            size={28}
          />
          <div className="leading-tight min-w-0">
            <div className="truncate text-label-medium text-text-primary">
              {row.name ?? "--"}
            </div>
            <div className="truncate text-label-small text-text-secondary">
              {row.email ?? "--"}
            </div>
          </div>
        </div>
      ),
    },
    {
      // Groups can hold both SSO-provisioned and locally-created users, so the
      // origin needs to be visible per row.
      field: "isSsoUser",
      headerName: "SSO",
      width: 90,
      renderCell: ({ row }) =>
        row.isSsoUser ? (
          <CheckIcon
            aria-label="SSO user"
            className="size-4 shrink-0 text-text-positive"
          />
        ) : (
          <XIcon
            aria-label="Not an SSO user"
            className="size-4 shrink-0 text-text-secondary"
          />
        ),
    },
    {
      field: "hasApiKeys",
      headerName: "API Keys",
      width: 120,
      renderCell: ({ row }) =>
        row.hasApiKeys ? (
          <CheckIcon
            aria-label="Has API keys"
            className="size-4 shrink-0 text-text-positive"
          />
        ) : (
          <XIcon
            aria-label="No API keys"
            className="size-4 shrink-0 text-text-secondary"
          />
        ),
    },
    {
      field: "lastLogin",
      headerName: "Last Login",
      type: "dateTime",
      width: 160,
      valueGetter: (params) => (params.value ? new Date(params.value) : null),
      renderCell: ({ row }) => <HumanTimestamp timestamp={row.lastLogin} />,
    },
    {
      field: "actions",
      headerName: "",
      width: 64,
      sortable: false,
      align: "right",
      renderCell: ({ row }) => (
        <Link
          className="inline-flex text-text-secondary hover:text-text-primary"
          textDecoration="none"
          to={`/settings/access-control/users/${row.key}`}
        >
          <ArrowRightIcon
            aria-label={`View ${row.name ?? row.email ?? "user"}`}
            size={16}
          />
        </Link>
      ),
    },
  ];

  if (onTransfer) {
    columns.unshift({
      field: "transfer",
      headerName: "",
      // One icon button, the same on every row, so the column never needs to
      // be any wider than the button. min and max pin it against the grid
      // sizing it from content; FIXED_WIDTH_FIELDS keeps autosize off it too.
      width: TRANSFER_COLUMN_WIDTH,
      minWidth: TRANSFER_COLUMN_WIDTH,
      maxWidth: TRANSFER_COLUMN_WIDTH,
      sortable: false,
      resizable: false,
      hideable: false,
      disableColumnMenu: true,
      align: "center",
      renderCell: ({ row }) => {
        const label = row.name ?? row.email ?? "user";
        return (
          <V2Tooltip
            title={isTransferDisabled ? (transferDisabledReason ?? "") : ""}
            asChild
          >
            <span className="inline-flex">
              <V2Button
                IconLeft={
                  isMemberList ? (
                    <MinusIcon size={14} />
                  ) : (
                    <PlusIcon size={14} />
                  )
                }
                aria-label={
                  isMemberList
                    ? `Remove ${label} from the group`
                    : `Add ${label} to the group`
                }
                isDisabled={isTransferDisabled}
                onPress={() =>
                  onTransfer({ id: row.id, uuid: row.uuid, label })
                }
                type="button"
                variant="plain"
              />
            </span>
          </V2Tooltip>
        );
      },
    });
  }

  // Snapshot the export params when the export starts rather than deriving them
  // live from `search`: the export hook re-runs its mutation whenever `params`
  // changes, so a search edit mid-export would otherwise start a second export
  // and retarget the tracked export ID onto a different CSV.
  const handleExport = () => {
    const filter = buildFilter(groupName, search, scope);
    setExportParams(
      filter
        ? [
            {
              name: "filterElement",
              type: "FilterElementInput",
              valueJSON: JSON.stringify(filter),
            },
          ]
        : undefined,
    );
    setExportInProgress(true);
  };

  // Debounce the live search, then apply it and reset to the first page. The
  // filter itself is built at navigation time from `appliedSearchRef`.
  const handleSearchChange = (value: string) => {
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      appliedSearchRef.current = value;
      goToFirstPage();
    }, SEARCH_DEBOUNCE_MS);
  };

  const hasHeader = title !== null || description !== null;

  return (
    <div className="flex flex-col">
      {title === null ? null : (
        <div className="border-b border-overlay-medium pb-xs">
          <V2Heading
            className="text-label-medium-strong text-text-primary"
            level={3}
          >
            {title}
          </V2Heading>
        </div>
      )}
      {description === null ? null : (
        <p className="mt-sm text-label-small text-text-secondary">
          {description}
        </p>
      )}

      {enableExport ? (
        <ExportCsv
          columns={CSV_COLUMNS}
          exportInProgress={exportInProgress}
          field="users"
          markComplete={() => setExportInProgress(false)}
          nodeId={null}
          params={exportParams}
          rowCount={total ?? rows.length}
        />
      ) : null}

      <div className={hasHeader ? "mt-md" : ""}>
        <StackletGrid
          className={GRID_CLASS_NAMES}
          columnHeaderHeight={36}
          columns={columns}
          defaultDensity="comfortable"
          exportInProgress={exportInProgress}
          fixedWidthFields={FIXED_WIDTH_FIELDS}
          getRowClassName={() => ""}
          getRowId={(row) => row.id}
          handleExport={enableExport ? handleExport : undefined}
          hasToolbar={!isSearchControlled}
          loading={isPending}
          onSearchChange={isSearchControlled ? undefined : handleSearchChange}
          pagination={{
            pageSize,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            onPageSizeChange: handlePageSizeChange,
            hasNextPage,
            hasPreviousPage,
            onNextPage: handleNext,
            onPreviousPage: handlePrevious,
            rangeStart,
            rangeEnd,
            total,
            unit: "user",
            isDisabled: isPending,
          }}
          rowHeight={52}
          rows={rows}
          search={search}
          searchAriaLabel="Search users"
          tableId={tableId}
          hideDensityButton
        />
      </div>
    </div>
  );
}
